import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, ArrowRight, Droplets, Waves, Layers,
  TrendingUp, Landmark, ShieldCheck, AlertTriangle, Sprout, Handshake, CheckCircle2,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const TOTAL = 7;

const Slide = ({ children, n, kicker, testid }) => (
  <div
    data-testid={testid}
    className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] mx-auto mb-8 print:mb-0 relative flex flex-col"
    style={{ breakAfter: n === TOTAL ? "auto" : "page", padding: "16mm" }}
  >
    <div className="flex items-center justify-between border-b border-border pb-3">
      <div className="flex items-center gap-2">
        <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
        <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVESTCO.</span>
      </div>
      <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{kicker}</span>
    </div>
    <div className="flex-1 min-h-0 pt-6 flex flex-col">{children}</div>
    <div className="pt-3 border-t border-border flex items-center justify-between text-[10px] text-muted-foreground">
      <span>Land Stewardship for Operators · Resource Preservation</span>
      <span>{n} / {TOTAL}</span>
    </div>
  </div>
);

const Bullet = ({ children }) => (
  <div className="flex items-start gap-2.5">
    <CheckCircle2 className="h-4 w-4 text-flame flex-shrink-0 mt-[2px]" />
    <span className="text-charcoal text-[13px] leading-snug">{children}</span>
  </div>
);

export default function LandStewardshipDeck() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/land-stewardship" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to stewardship
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Operator Stewardship · {TOTAL}-Slide Deck</span>
            <a href="/land-stewardship-deck.pdf" download data-testid="download-stew-deck" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-stew-deck" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      <div className="py-8 print:py-0" style={{ fontSize: "12px" }}>

        {/* 1 · TITLE */}
        <Slide n={1} kicker="For Operators" testid="stew-slide-1">
          <div className="flex-1 flex flex-col justify-center">
            <span className="inline-flex w-fit items-center border border-flame/40 bg-flame/10 text-flame px-3 py-1 text-[11px] uppercase tracking-[0.2em]">Land Stewardship · Resource Preservation</span>
            <h1 className="mt-5 font-display font-black tracking-tight text-ink leading-[0.98] text-[58px] max-w-[88%]">
              Steward the land<br />you operate on.
            </h1>
            <p className="mt-5 text-charcoal text-[18px] leading-snug max-w-[72%]">
              A leave-behind for operations, HSE, and land teams: how protecting the ground under today's projects protects your landowner relationships, your liability position, and your legacy — without slowing the job.
            </p>
            <div className="mt-8 flex items-center gap-3 text-[13px] text-muted-foreground">
              <span className="font-display font-bold text-ink">{COMPANY.legal}</span> · {COMPANY.phone} · {COMPANY.email}
            </div>
          </div>
        </Slide>

        {/* 2 · THE SHIFT */}
        <Slide n={2} kicker="The shift" testid="stew-slide-2">
          <h2 className="font-display font-black text-ink text-[38px] leading-tight max-w-[85%]">Your project is near-term. The land is a long-cycle asset.</h2>
          <p className="mt-3 text-charcoal text-[15px] max-w-[80%]">You optimize for production and schedule — as you should. But the ground answers on a longer clock, and how a site is treated today sets what it's worth in the next cycle.</p>
          <div className="mt-8 grid grid-cols-3 gap-6 flex-1">
            {[
              { icon: TrendingUp, t: "Demand outpaces supply", d: "Capital is acquiring land for agriculture and livestock as long-term demand outruns available acreage." },
              { icon: Sprout, t: "Today's site, tomorrow's asset", d: "The ground under today's drilling and construction becomes rich, working land in future cycles." },
              { icon: Landmark, t: "Condition sets the value", d: "Healthy, uncontaminated land versus deteriorated land is the difference in what the asset is worth." },
            ].map((c) => (
              <div key={c.t} className="border border-border p-6 flex flex-col">
                <c.icon className="h-9 w-9 text-flame" />
                <div className="mt-4 font-display font-bold text-ink text-[18px]">{c.t}</div>
                <p className="mt-2 text-charcoal text-[13px] leading-snug">{c.d}</p>
              </div>
            ))}
          </div>
        </Slide>

        {/* 3 · THE RISK */}
        <Slide n={3} kicker="The exposure" testid="stew-slide-3">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-flame" />
            <h2 className="font-display font-black text-ink text-[36px] leading-tight">What poor land treatment costs an operator</h2>
          </div>
          <p className="mt-4 text-charcoal text-[15px] max-w-[85%]">Soil contamination, ruined topsoil, and impacted water don't just harm the land — they land back on your organization.</p>
          <div className="mt-6 grid grid-cols-2 gap-5 flex-1">
            {[
              ["Remediation & restoration cost", "Contaminated soil and rutted, compacted ground drive expensive cleanup and reclamation obligations."],
              ["Liability & compliance risk", "Spills that reach soil and groundwater create long-tail environmental liability and regulatory exposure."],
              ["Landowner friction & lost access", "Leaving land degraded damages trust — and future access, renewals, and referrals with landowners."],
              ["Reputation & stakeholder scrutiny", "Stakeholders and communities increasingly judge operators on how responsibly they treat the land."],
            ].map(([t, d]) => (
              <div key={t} className="border border-border p-6">
                <div className="font-display font-bold text-ink text-[17px]">{t}</div>
                <p className="mt-2 text-charcoal text-[13px] leading-snug">{d}</p>
              </div>
            ))}
          </div>
        </Slide>

        {/* 4 · THE OPPORTUNITY */}
        <Slide n={4} kicker="The opportunity" testid="stew-slide-4">
          <h2 className="font-display font-black text-ink text-[36px] leading-tight max-w-[85%]">Turn land care into visionary leadership.</h2>
          <p className="mt-4 text-charcoal text-[15px] max-w-[82%]">Stewardship isn't a cost of doing business — it's a differentiator your organization can own and put in front of stakeholders.</p>
          <div className="mt-6 grid grid-cols-2 gap-8 flex-1">
            <div className="space-y-3 pt-2">
              <Bullet>Add measurable value and enhanced, visionary leadership to your brand</Bullet>
              <Bullet>Strengthen landowner trust — protecting future access and renewals</Bullet>
              <Bullet>Reduce environmental liability across the project lifecycle</Bullet>
              <Bullet>Support ESG mandates with concrete, defensible actions</Bullet>
              <Bullet>Differentiate in bids where land treatment is scored</Bullet>
            </div>
            <div className="bg-ink text-white p-8 flex flex-col justify-center">
              <Handshake className="h-10 w-10 text-flame" />
              <p className="mt-5 font-display font-bold text-[22px] leading-snug">"We leave the land better than we found it."</p>
              <p className="mt-3 text-white/60 text-[13px] leading-snug">A simple, credible position — backed by the right products deployed on every site.</p>
            </div>
          </div>
        </Slide>

        {/* 5 · THE PORTFOLIO */}
        <Slide n={5} kicker="How we deliver" testid="stew-slide-5">
          <h2 className="font-display font-black text-ink text-[34px] leading-tight">One portfolio, built to protect the resource</h2>
          <div className="mt-6 grid grid-cols-3 gap-6 flex-1">
            {[
              { icon: Droplets, n: "BoneDry", c: "Absorbents", r: "Keeps hydrocarbons out of the soil", d: "Encapsulates oil & fuel on contact so spills never leach into the ground." },
              { icon: Waves, n: "Pinkwater", c: "Water Treatment", r: "Protects the water the land depends on", d: "Treats problem water on site so contaminants never reach the watershed." },
              { icon: Layers, n: "Big Bad Mats", c: "Ground Protection", r: "Preserves soil structure & topsoil", d: "Distributes heavy loads to prevent rutting and compaction; the ground recovers." },
            ].map((p) => (
              <div key={p.n} className="border border-border flex flex-col">
                <div className="bg-ink text-white px-5 py-4 flex items-center gap-3">
                  <p.icon className="h-7 w-7 text-flame" />
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.16em] text-flame">{p.c}</div>
                    <div className="font-display font-bold text-[18px]">{p.n}</div>
                  </div>
                </div>
                <div className="p-5 flex-1">
                  <div className="font-display font-bold text-ink text-[14px] leading-snug">{p.r}</div>
                  <p className="mt-2 text-charcoal text-[12.5px] leading-snug">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </Slide>

        {/* 6 · DUAL WIN */}
        <Slide n={6} kicker="Shared value" testid="stew-slide-6">
          <h2 className="font-display font-black text-ink text-[34px] leading-tight">A win on both sides of the lease</h2>
          <div className="mt-6 grid grid-cols-2 gap-6 flex-1">
            <div className="border border-border p-7 flex flex-col">
              <div className="flex items-center gap-3"><Landmark className="h-7 w-7 text-flame" /><h3 className="font-display font-bold text-[22px] text-ink">Landowners</h3></div>
              <p className="mt-4 text-charcoal text-[13px] leading-snug">Healthy, uncontaminated ground lets owners fully leverage the land for premium ag & livestock as demand outruns supply.</p>
              <div className="mt-4 space-y-2.5">
                <Bullet>Protected soil, topsoil & water table</Bullet>
                <Bullet>Preserved resale & lease value</Bullet>
                <Bullet>No remediation cost or contamination stigma</Bullet>
              </div>
            </div>
            <div className="border border-flame/40 bg-flame/[0.03] p-7 flex flex-col">
              <div className="flex items-center gap-3"><TrendingUp className="h-7 w-7 text-flame" /><h3 className="font-display font-bold text-[22px] text-ink">Operators</h3></div>
              <p className="mt-4 text-charcoal text-[13px] leading-snug">Stewardship becomes a value-add and leadership story that strengthens relationships and reduces risk.</p>
              <div className="mt-4 space-y-2.5">
                <Bullet>Visionary leadership & brand value</Bullet>
                <Bullet>Stronger landowner trust & future access</Bullet>
                <Bullet>Lower liability; ESG support</Bullet>
              </div>
            </div>
          </div>
        </Slide>

        {/* 7 · CTA */}
        <Slide n={7} kicker="Make it your standard" testid="stew-slide-7">
          <h2 className="font-display font-black text-ink text-[36px] leading-tight max-w-[80%]">Make land stewardship part of your program.</h2>
          <p className="mt-3 text-charcoal text-[15px] max-w-[75%]">We'll help you build a simple, repeatable preservation standard across your sites — matched to your operations, not bolted on.</p>
          <div className="mt-6 grid grid-cols-3 gap-6 flex-1">
            {[
              { icon: ShieldCheck, t: "Assess", d: "We review your sites, risks, and current practices." },
              { icon: Layers, t: "Equip", d: "Match BoneDry, Pinkwater & Big Bad Mats to each site." },
              { icon: Sprout, t: "Demonstrate", d: "Document the stewardship story for stakeholders." },
            ].map((c) => (
              <div key={c.t} className="border border-border p-6 flex flex-col">
                <c.icon className="h-9 w-9 text-flame" />
                <div className="mt-4 font-display font-bold text-ink text-[17px]">{c.t}</div>
                <p className="mt-2 text-charcoal text-[13px] leading-snug">{c.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 bg-ink text-white px-8 py-6 flex items-center justify-between">
            <div>
              <div className="font-display font-black text-[24px] leading-tight">Lead on land. Protect the asset.</div>
              <div className="text-white/60 text-[13px] mt-1">Talk to your Environmental Solutions Manager — {COMPANY.phone} · {COMPANY.email}</div>
            </div>
            <Link to="/request-quote" className="inline-flex items-center gap-2 bg-flame text-white px-6 py-3 font-semibold text-[15px] hover:bg-flame/90 transition-colors print:hidden">
              Build a Plan <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Slide>

        <p className="max-w-6xl mx-auto px-6 mt-2 text-[10px] text-muted-foreground print:hidden">
          © {new Date().getFullYear()} {COMPANY.legal}. Informational overview for operator stakeholders; not a contract or guarantee of any specific environmental result.
        </p>
      </div>
    </div>
  );
}
